import { Core } from "@faire/design-tokens";
import { trackCategoriesCategoryHomepageShopByCategoryClick } from "@faire/web-api/events/categories/click/categoryHomepageShopByCategory";
import { trackCategoriesCategoryHomepageShopByCategoryView } from "@faire/web-api/events/categories/view/categoryHomepageShopByCategory";
import { ICategoryTile } from "@faire/web-api/indigofair/data/ICategoryTile";
import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { Column, Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { Link } from "@faire/web/ui/LinkAnchor";
import { WhenVisibilityIsImminent } from "@faire/web/ui/VisibilitySensor/WhenVisibilityIsImminent";
import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";

import { categoryPath } from "@faire/retailer/paths/category/categoryPath";
import { useCategoryStore } from "@faire/retailer/stores/domain/CategoryStore";
import { findClosestActiveCategoryUrlIdsByPathIdentifier } from "@faire/retailer/stores/domain/CategoryStore/findByPathIdentifier";

interface IProps {
  title: string;
  categoryTiles: ICategoryTile[];
  categoryPathIdentifier: string;
  className?: string;
  onVisible?: (token: string) => void;
  onItemClick?: (tokens: string) => void;
}

export const ShopByCategory: React.FC<IProps> = ({
  title,
  categoryTiles,
  categoryPathIdentifier,
  className,
  onVisible,
  onItemClick,
}) => {
  const viewport = useViewport();

  const handleComponentVisible = useDoActionWhenVisible(() => {
    trackCategoriesCategoryHomepageShopByCategoryView(categoryPathIdentifier);
    onVisible?.(
      categoryTiles.map((tile) => tile.category_path_identifier).join(" ")
    );
  });

  return (
    <WhenVisibilityIsImminent customizePlaceholder={<Placeholder />}>
      <Wrapper
        className={className}
        data-test-id="categoryhomepage-shop-by-category"
        ref={handleComponentVisible}
      >
        <Title>{title}</Title>
        <Spacer size={viewport.isTabletAndAbove ? "3x" : "1x"} />
        <CategoryTileWrapper
          $maxColumns={viewport.isTabletAndAbove ? 4 : 2}
          $horizontalPadding={useResponsiveValue({
            mobileAndAbove: 4,
            tabletAndAbove: 5,
            desktopAndAbove: 8,
          })}
        >
          {categoryTiles.map((categoryTile) => (
            <CategoryTile
              key={categoryTile.url_id ?? categoryTile.category_path_identifier}
              categoryTile={categoryTile}
              categoryCarouselName={categoryPathIdentifier}
              largeImage={categoryTiles.length < 4}
              onClick={onItemClick}
            />
          ))}
        </CategoryTileWrapper>
      </Wrapper>
    </WhenVisibilityIsImminent>
  );
};

const CategoryTileBase: React.FC<{
  categoryTile: ICategoryTile;
  categoryCarouselName: string;
  largeImage?: boolean;
  onClick?: (token: string) => void;
}> = ({ categoryTile, categoryCarouselName, largeImage, onClick }) => {
  const isSSR = useIsSSR();
  const { categories } = useCategoryStore();
  if (
    !categoryTile?.image ||
    !categoryTile.name ||
    !categoryTile.category_path_identifier
  ) {
    return null;
  }
  const categoryUrlIds = findClosestActiveCategoryUrlIdsByPathIdentifier(
    categoryTile.category_path_identifier,
    categories
  );

  if (categoryUrlIds === undefined) {
    return null;
  }

  return (
    <TileWrapper
      $backgroundUrl={optimizeImageUrl(categoryTile?.image, isSSR, {
        width: largeImage ? 310 : 230,
        height: 96,
        resizeMethod: "fill",
      })}
      to={categoryPath({ urlIds: categoryUrlIds })}
      onClick={() => {
        trackCategoriesCategoryHomepageShopByCategoryClick(
          categoryCarouselName,
          categoryTile.category_path_identifier ?? ""
        );
        onClick?.(categoryTile.category_path_identifier ?? "");
      }}
    >
      <Name>{categoryTile.name}</Name>
    </TileWrapper>
  );
};
const CategoryTile = observer(CategoryTileBase);

const Wrapper = styled(Column)`
  ${responsiveMediaQueries.mobile} {
    padding: 0 18px;
  }
`;

const TileWrapper = styled(Link)<{
  $backgroundUrl?: string;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  background-image: url(${(props) => props.$backgroundUrl});
  background-size: cover;
  background-position: center;
  text-align: center;
  width: 100%;
  height: 56px;
  ${responsiveMediaQueries.tabletAndAbove} {
    height: 96px;
  }
`;

const Title = styled(Typography).attrs({
  variant: "pageHeaderSerifRegular",
})`
  width: 100%;
  display: flex;
  ${responsiveMediaQueries.tabletAndAbove} {
    justify-content: "left";
  }
`;

const Name = styled(Typography).attrs({
  color: Core.text.inverse,
  variant: "paragraphSansMedium",
})`
  &&& {
    font-size: 16px;
    ${responsiveMediaQueries.tabletAndAbove} {
      font-size: 20px;
    }
  }
`;

const Placeholder = styled.div`
  height: 204px;
  width: 100%;
  ${responsiveMediaQueries.tabletAndAbove} {
    height: 196px;
  }
`;

const CategoryTileWrapper = styled(Row).attrs({ justify: "center" })<{
  $maxColumns: number;
  $horizontalPadding: number;
}>`
  width: 100%;
  flex-wrap: wrap;

  ${TileWrapper} {
    flex: 1 0 ${(props) => 100 / props.$maxColumns - 4}%;
    margin: 4px ${(props) => props.$horizontalPadding}px;

    ${responsiveMediaQueries.mobile} {
      max-width: ${(props) => 100 / props.$maxColumns}%;
    }
  }
`;
